import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import SubTitle from "../common/SubTitle";
import StopAddMap from "./StopAddMap";
import StopAddDetails from "./StopAddDetails";
import config from "../../config.js";
import { Box, Skeleton } from "@mui/material";
import { useUserInfo } from "../../hooks/useUserInfo";
import { useTranslation } from "react-i18next";

export default function StopsAddMain() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { stopId } = useParams();

  const [routesData, setRoutesData] = useState([]);
  const [labels, setLabels] = useState([]);
  const [selectedLabelId, setSelectedLabelId] = useState("");
  const [initialStopData, setInitialStopData] = useState({
    stopId: "",
    name: "",
    longitude: "",
    latitude: "",
    landmark: "",
    description: "",
  });
  const orgName = JSON.parse(localStorage.getItem("userState"))?.organization
    ?.name;

  const path = stopId
    ? `${orgName || "organization"} > Stops > ${initialStopData?.name}`
    : `${orgName || "organization"} > Stops > Create`;
  const [position, setPosition] = useState({ latitude: "", longitude: "" });
  const stop_base_url = `${config.baseURL}/stop/`;
  const route_base_url = `${config.baseURL}/route/`;
  const [organizationId, setOrganizationId] = useState(null);
  const [userFeatures, setUserFeatures] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { userId } = useUserInfo();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const userState = localStorage.getItem("userState");
        const parsedUserState = JSON.parse(userState);
        if (parsedUserState?.user?.organizationId) {
          setOrganizationId(parsedUserState.user.organizationId);
        }
        if (parsedUserState?.features) {
          setUserFeatures(parsedUserState.features);
        }
        let [routesResponse, stopResponse, labelsResponse] = await Promise.all([
          axios.get(`${route_base_url}`),
          stopId
            ? axios.get(`${config.baseURL}/stop/id`, { params: { id: stopId } })
            : Promise.resolve(null),
          axios.get(`${config.baseURL}/label`, { withCredentials: true }),
        ]);

        console.log(routesResponse, stopResponse);
        setRoutesData(routesResponse.data);
        const labelsData = labelsResponse.data;
        setLabels(
          Array.isArray(labelsData)
            ? labelsData
            : (labelsData?.labels ?? labelsData?.data ?? []),
        );
        if (stopResponse) {
          const stopData = stopResponse.data;
          setInitialStopData({
            stopId: stopData.stopId,
            name: stopData.name,
            longitude: stopData.longitude,
            latitude: stopData.latitude,
            landmark: stopData.landmark || "",
            description: stopData.description || "",
          });
          setPosition({
            latitude: stopData.latitude,
            longitude: stopData.longitude,
          });
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [stopId]);

  const hasPermission = (apiPath, action) => {
    return userFeatures.some(
      (feature) => feature.api_path === apiPath && feature.action === action,
    );
  };

  const canViewStopSchedules = hasPermission("/api/schedule/stop", "POST");

  const updateMapPosition = (lat, lng) => {
    setPosition({
      latitude: lat,
      longitude: lng,
    });
  };

  const handleSubmit = async (stopDetails) => {
    try {
      const stopData = {
        ...stopDetails,
        organizationId: organizationId,
        longitude: position.longitude,
        latitude: position.latitude,
        performedByUserId: userId,
      };

      let savedStopId = stopId;

      if (stopId) {
        // Update existing stop
        await axios.put(
          `${stop_base_url}`,
          { ...stopData, id: stopId },
          {
            withCredentials: true,
          },
        );
      } else {
        // Create new stop
        const resp = await axios.post(`${stop_base_url}`, stopData, {
          withCredentials: true,
        });
        console.log("response", resp.data);
        savedStopId = resp.data?.id ?? resp.data?.stop?.id ?? resp.data?.stopId;
      }

      // Assign label if one was selected
      if (selectedLabelId && savedStopId) {
        await axios.post(
          `${config.baseURL}/label/${selectedLabelId}/assign`,
          { entityType: "stops", entityId: savedStopId },
          { withCredentials: true },
        );
      }

      navigate("/stop");
    } catch (error) {
      console.error("Error saving stop:", error);
    }
  };

  const handleStopSchedules = () => {
    if (stopId) {
      navigate("/stop/Schedule/Details", {
        state: { id: stopId },
      });
    }
    // Do nothing if stopId doesn't exist (creating new stop)
  };

  return (
    <>
      <SubTitle
        title={t(stopId ? "Update Stop" : "Create Stops")}
        path={path}
        {...(stopId && canViewStopSchedules
          ? {
              track: t("View Schedules"),
              trackPath: "/stop/stopschedules",
              onTrackClick: handleStopSchedules,
            }
          : {})}
      />
      {isLoading ? (
        <Box sx={{ p: 2 }}>
          <Skeleton
            variant="rectangular"
            width="100%"
            height={300}
            sx={{ mb: 3, borderRadius: 2 }}
          />
          <Skeleton
            variant="rectangular"
            width="100%"
            height={300}
            sx={{ mb: 3, borderRadius: 2 }}
          />
        </Box>
      ) : (
        <>
          <StopAddDetails
            initialData={initialStopData}
            onSubmit={handleSubmit}
            position={position}
            updateMapPosition={updateMapPosition}
            labels={labels}
            selectedLabelId={selectedLabelId}
            onLabelChange={setSelectedLabelId}
          />
        </>
      )}
      <Box sx={{ height: "40px" }}></Box>
    </>
  );
}
